import {
  closeAccountPortal,
  logoutUser,
  reAuthenticateUser,
  useBasicUser,
  useLoginStatus,
} from '@account/account-portal/lib'
import { LoginCredentials, LoginError } from '@account/account-portal/types'
import { Consents } from '@account/consents'
import { GlButton } from '@adl/component-button'
import { GlForm } from '@adl/component-form'
import { useConfig } from '@crystal/config'
import { useDevice, useMfProps } from '@crystal/core'
import { SubmitHandler, useFormValidation } from '@crystal/form'
import { useTranslations } from '@crystal/translator'
import { useState } from 'react'

import { AccountPortalConfig } from '../config'
import { trackFreshAuthenticationOverlayConfirm, trackFreshAuthenticationOverlayLogout } from '../lib/analytics'

import styles from './account-portal.module.scss'
import { FormContainer } from './layout'
import LoadingScreen from './LoadingScreen'
import { PasswordInput } from './PasswordInput'
import { SocialLogin } from './SocialLogin'

export const ReAuthentication = () => {
  const t = useTranslations()
  const isSSR = typeof window === 'undefined'
  const user = useBasicUser()

  if (!user) {
    if (isSSR) {
      return <LoadingScreen />
    }
    logoutUser()
    return null
  }

  const { registerField, handleSubmit, setError } = useFormValidation<LoginCredentials>()
  const { passwordPolicyLogin } = useConfig<AccountPortalConfig>()
  const [generalError, setGeneralError] = useState<string>()
  const { isPending } = useLoginStatus()
  const { isMobile } = useDevice()
  const mfProps = useMfProps()

  function showApiErrors({ validationErrors, details }: LoginError) {
    if (details?.statusCode === 400 && validationErrors?.error === 'invalid_grant') {
      setGeneralError('')
      setError('password', { message: t('login.error') })
    } else if (details?.statusCode === 401 && validationErrors) {
      setGeneralError('')
      setError('password', { message: t('password.error.min.age', t('global.country')) })
    } else {
      setGeneralError(t('unknownError.message'))
    }
  }

  const onSubmit: SubmitHandler<LoginCredentials> = async ({ password }) => {
    const credentials: LoginCredentials = {
      user: user.userName,
      password,
    }
    await reAuthenticateUser(credentials)
      .then(() => {
        trackFreshAuthenticationOverlayConfirm()
        closeAccountPortal()
      })
      .catch(err => showApiErrors(err as LoginError))
  }

  const onLogout = async () => {
    trackFreshAuthenticationOverlayLogout()
    await logoutUser()
    closeAccountPortal()
  }

  return (
    <FormContainer>
      <h1 className={styles['title']}>{t('account.reauthenticate.overlay.title')}</h1>
      <p className={styles['subtitle']}>{t('account.reauthenticate.overlay.description')}</p>
      <GlForm noValidate onSubmit={handleSubmit(onSubmit)}>
        {user.session.socialProvider ? (
          <SocialLogin persistentLogin={user.session.isPersistent} />
        ) : (
          <>
            <PasswordInput
              id="password"
              className="gl-form-item"
              labelText={t('password.label')}
              aria-label={t('password.label')}
              {...registerField('password', {
                ...passwordPolicyLogin,
                errorMessage: t('password.hint'),
              })}
            />
            <GlButton
              fullWidth={isMobile}
              type="submit"
              className="gl-vspace-bpall-small"
              aria-label={t('registerButton.label')}
              loading={isPending}
              disabled={isPending}
              id="login-submit-button">
              {t('account.reauthenticate.overlay.confirm')}
            </GlButton>
          </>
        )}
      </GlForm>
      <Consents {...mfProps} implicit scenarioType="login" />
      {generalError && <p className={styles['error']}>{generalError}</p>}
      <GlButton variant="tertiary" data-auto-id="glass-account-logout" onClick={onLogout}>
        {t('account.reauthenticate.overlay.logout')}
      </GlButton>
    </FormContainer>
  )
}
